import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Grid,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useTranslation } from "react-i18next";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const SaleEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/sales", token);

  // Local state for sale-level fields.
  const [shop, setShop] = useState("");
  const [customer, setCustomer] = useState("");
  const [discount, setDiscount] = useState("");
  const [saleDate, setSaleDate] = useState("");
  const [saleCurrency, setSaleCurrency] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});

  // Fetch dropdown data.
  const { data: shopsData, isLoading: shopLoading } = useFetchObjects(
    ["shop", "sale-edit"],
    "shop/shop/?page_size=all",
    token
  );
  const { data: customers, isLoading: customerLoading } = useFetchObjects(
    ["customer", "sale-edit"],
    "shop/customers/?page_size=all",
    token
  );
  // Products may be used for fallback, but primary product options come from stocks.
  const { data: products, isLoading: productLoading } = useFetchObjects(
    ["product", "sale-edit"],
    "shop/products/?page_size=all",
    token
  );
  const { data: units, isLoading: unitLoading } = useFetchObjects(
    ["unit", "sale-edit"],
    "shop/units/?page_size=all",
    token
  );
  const { data: currencies, isLoading: currencyLoading } = useFetchObjects(
    ["currency", "sale-edit"],
    "shop/currencies/?page_size=all",
    token
  );
  const { data: exchangeRatesData } = useFetchObjects(
    ["sale-edit-exchange-rates"],
    "shop/exchange-rates/?page_size=1000",
    token
  );

  // Fetch stocks for the selected shop.
  const [stocks, setStocks] = useState([]);
  const {
    data: stocksData,
    isLoading: stockLoading,
    refetch: refetchStocks,
  } = useFetchObjects(
    ["stock", "sale", shop],
    shop ? `shop/stocks/?shop=${shop}&page_size=all` : "",
    token
  );

  const shopsList = shopsData?.results ?? shopsData ?? [];
  const customersList = customers?.results ?? customers ?? [];
  const getCustomerShopId = React.useCallback(
    (customer) => customer?.shop ?? customer?.shop_details?.id ?? "",
    []
  );
  const filteredCustomersList = useMemo(
    () =>
      shop
        ? customersList.filter((customerRow) => String(getCustomerShopId(customerRow)) === String(shop))
        : customersList,
    [customersList, getCustomerShopId, shop]
  );
  const productsList = products?.results ?? products ?? [];
  const unitsList = units?.results ?? units ?? [];
  const allCurrenciesList = currencies?.results ?? currencies ?? [];
  const currenciesList = shop
    ? allCurrenciesList.filter((currency) => String(currency?.shop) === String(shop))
    : getUniqueCurrencies(allCurrenciesList);
  const selectedCurrencyTitle = React.useMemo(() => {
    const selected = currenciesList.find((c) => String(c.id) === String(saleCurrency));
    const fallback = allCurrenciesList.find((c) => String(c.id) === String(saleCurrency));
    return (selected?.title || fallback?.title || "").toUpperCase();
  }, [allCurrenciesList, currenciesList, saleCurrency]);
  const normalizeCurrencyCode = React.useCallback((value) => {
    const code = String(value || "").trim().toUpperCase();
    if (code === "AFN" || code === "AFG" || code === "؋" || code.includes("AFGHAN") || code.includes("افغ")) return "AFG";
    if (code === "USD" || code === "$" || code.includes("DOLLAR") || code.includes("دالر")) return "USD";
    return code;
  }, []);
  const firstPositivePrice = React.useCallback((...values) => {
    const value = values.find((item) => {
      const amount = Number(item);
      return item !== null && item !== undefined && item !== "" && Number.isFinite(amount) && amount > 0;
    });
    return value ?? "";
  }, []);
  const pickPriceByCurrency = React.useCallback((details, baseField, currencyTitle, fallback) => {
    const code = normalizeCurrencyCode(currencyTitle);
    const suffix = code === "USD" ? "_usd" : "_afg";
    const v = details?.[`${baseField}${suffix}`];
    if (v !== null && v !== undefined && v !== "" && Number(v) > 0) return v;
    return fallback;
  }, [normalizeCurrencyCode]);

  const latestRateMap = React.useMemo(() => {
    const rows = exchangeRatesData?.results || exchangeRatesData || [];
    const map = new Map();
    rows.forEach((rate) => {
      const from = normalizeCurrencyCode(rate?.from_currency_title || rate?.from_currency?.title);
      const to = normalizeCurrencyCode(rate?.to_currency_title || rate?.to_currency?.title);
      const value = Number(rate?.rate);
      if (!from || !to || !Number.isFinite(value) || value <= 0) return;
      const key = `${from}->${to}`;
      const prev = map.get(key);
      const rateDate = String(rate?.rate_date || "");
      if (!prev || rateDate > prev.rateDate) {
        map.set(key, { rate: value, rateDate });
      }
    });
    return map;
  }, [exchangeRatesData, normalizeCurrencyCode]);

  const convertPrice = React.useCallback((amount, fromCurrency, toCurrency) => {
    const from = normalizeCurrencyCode(fromCurrency);
    const to = normalizeCurrencyCode(toCurrency);
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return null;
    if (!from || !to || from === to) return numericAmount;
    const direct = latestRateMap.get(`${from}->${to}`);
    if (direct?.rate) return numericAmount * direct.rate;
    const reverse = latestRateMap.get(`${to}->${from}`);
    if (reverse?.rate) return numericAmount / reverse.rate;
    const fallbackRate = Number(exchangeRate);
    if (Number.isFinite(fallbackRate) && fallbackRate > 0) {
      if (from === "AFG" && to === "USD") return numericAmount / fallbackRate;
      if (from === "USD" && to === "AFG") return numericAmount * fallbackRate;
    }
    return null;
  }, [exchangeRate, latestRateMap, normalizeCurrencyCode]);

  const getStockPriceByCurrency = React.useCallback((stockRecord, matchingStocks, baseField, fallback) => {
    const selectedCode = normalizeCurrencyCode(selectedCurrencyTitle);
    const details = stockRecord?.purchase_item_details || {};
    const directValue = pickPriceByCurrency(details, baseField, selectedCode, "");
    if (directValue !== null && directValue !== undefined && directValue !== "" && Number(directValue) > 0) return directValue;

    const matchingDirectValue = (matchingStocks || [])
      .map((stock) => pickPriceByCurrency(stock?.purchase_item_details || {}, baseField, selectedCode, ""))
      .find((value) => value !== null && value !== undefined && value !== "" && Number(value) > 0);
    if (matchingDirectValue !== null && matchingDirectValue !== undefined && matchingDirectValue !== "") return matchingDirectValue;

    const stockWithCurrency = (matchingStocks || []).find(
      (stock) => normalizeCurrencyCode(stock?.currency_details?.title) === selectedCode
    );
    const currencyDetails = stockWithCurrency?.purchase_item_details || {};
    const currencyValue =
      currencyDetails?.[baseField] ??
      (baseField === "retail_price" ? currencyDetails?.sale_price : null);
    if (currencyValue !== null && currencyValue !== undefined && currencyValue !== "" && Number(currencyValue) > 0) return currencyValue;

    const oppositeCode = selectedCode === "USD" ? "AFG" : "USD";
    const oppositeValue = pickPriceByCurrency(details, baseField, oppositeCode, "");
    const convertedOppositeValue = convertPrice(oppositeValue, oppositeCode, selectedCode);
    if (convertedOppositeValue !== null) return convertedOppositeValue.toFixed(2);

    const stockCurrency = normalizeCurrencyCode(stockRecord?.currency_details?.title);
    const baseValue =
      details?.[baseField] ??
      (baseField === "retail_price" ? details?.sale_price || stockRecord?.sale_price : null) ??
      fallback;
    const convertedBaseValue = convertPrice(baseValue, stockCurrency, selectedCode);
    if (convertedBaseValue !== null) return convertedBaseValue.toFixed(2);

    return fallback;
  }, [convertPrice, normalizeCurrencyCode, pickPriceByCurrency, selectedCurrencyTitle]);

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "sale-edit", shop],
    shop ? `shop/accounts/?page_size=all&shop=${shop}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(saleCurrency)),
    [accounts, saleCurrency]
  );

  const { data: directRateData } = useFetchObjects(
    ["sale-edit-rate-direct", shop, saleCurrency, selectedAccount?.currency],
    shop && saleCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${saleCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["sale-edit-rate-reverse", shop, saleCurrency, selectedAccount?.currency],
    shop && saleCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${selectedAccount.currency}&to_currency=${saleCurrency}`
      : "",
    token
  );

  const getStockShopId = (stock) => String(stock?.shop || stock?.shop_details?.id || "");
  const getStockQuantity = (stock) => Number(stock?.quantity || stock?.availableQty || 0) || 0;
  // Prefer product id so different products that share a code still appear in sale.
  const getProductDedupeKey = (product) => {
    if (product?.id != null && product.id !== "") return `id:${product.id}`;
    const code = String(product?.code || product?.product_code || "").trim().toUpperCase();
    if (code) return `code:${code}`;
    return `name:${String(product?.name || "").trim().replace(/\s+/g, " ").toUpperCase()}`;
  };

  const uniqueProductsFromStocks = React.useMemo(() => {
    const prodMap = new Map();
    (stocks || [])
      .filter((stock) => getStockQuantity(stock) > 0 && (!shop || getStockShopId(stock) === String(shop)))
      .forEach((stock) => {
        const prod = stock.purchase_item_details?.product;
        if (!prod?.id) return;
        const key = getProductDedupeKey(prod);
        const current = prodMap.get(key);
        if (!current || getStockQuantity(stock) > getStockQuantity(current.stock)) {
          prodMap.set(key, { product: { id: prod.id, name: prod.name || `Product ${prod.id}`, code: prod.code }, stock });
        }
      });
    editData?.sale_items?.forEach((item) => {
      const pid = item.product ?? item.product_details?.id;
      if (!pid) return;
      const product = { id: pid, name: item.product_details?.name || `Product ${pid}`, code: item.product_details?.code };
      const key = getProductDedupeKey(product);
      if (!prodMap.has(key)) prodMap.set(key, { product, stock: null });
    });
    return Array.from(prodMap.values()).map((entry) => entry.product);
  }, [stocks, editData, shop]);

  const getProductOptionLabel = React.useCallback((product) => {
    if (!product) return "";
    const name = product.name || "";
    const code = product.code ? ` (${product.code})` : "";
    return `${name}${code}`;
  }, []);

  useEffect(() => {
    if (stocksData) {
      const list = stocksData?.results ?? stocksData;
      setStocks(Array.isArray(list) ? list : []);
    } else {
      setStocks([]);
    }
  }, [stocksData]);

  useEffect(() => {
    if (shop) {
      refetchStocks();
    } else {
      setStocks([]);
    }
  }, [shop, refetchStocks]);

  // Populate initial values when editData or open changes (so we reset when dialog opens with new data).
  useEffect(() => {
    if (open && editData) {
      const shopId = editData.shop != null ? editData.shop : editData.shop_details?.id;
      const customerId = editData.customer != null ? editData.customer : editData.customer_details?.id;
      const currencyId = editData.currency != null ? editData.currency : editData.currency_details?.id;

      setShop(shopId != null ? String(shopId) : "");
      setCustomer(customerId != null ? String(customerId) : "");

      // Load amount directly
      setDiscount(editData.amount != null && editData.amount !== "" ? String(editData.amount) : "");

      setSaleDate(
        editData.sale_date ? String(editData.sale_date).substring(0, 10) : ""
      );
      setSaleCurrency(currencyId != null ? String(currencyId) : "");
      setReceivedAmount(editData.received_amount != null ? String(editData.received_amount) : "");
      setSelectedAccountId(editData.auto_payment_details?.account ? String(editData.auto_payment_details.account) : "");
      setExchangeRate(editData.auto_payment_details?.exchange_rate || "");
      setPaymentNotes(editData.auto_payment_details?.notes || "");
      setNotes(editData.notes != null ? String(editData.notes) : "");
      if (editData.sale_items?.length) {
        const initialItems = editData.sale_items.map((item) => {
          const qty = Number(item.quantity) || 0;
          const stockQty = Number(item.product_details?.stock_quantity) || 0;
          const productId =
            (item.product != null ? item.product : item.product_details?.id) != null
              ? String(item.product != null ? item.product : item.product_details.id)
              : "";
          return {
            id: item.id,
            product: productId,
            original_product: productId,
            original_quantity: qty,
            quantity: item.quantity,
            unit:
              (item.unit != null ? item.unit : item.unit_details?.id) != null
                ? String(item.unit != null ? item.unit : item.unit_details.id)
                : "",
            sale_price: item.sale_price,
            buy_price: item.buy_price || item.purchase_details?.buy_price || "",
            purchase_item: item.purchase_item || "",
            // Current stock already excludes this sale line — add it back for edit validation.
            available: stockQty + qty,
          };
        });
        setItems(initialItems);
      } else {
        setItems([]);
      }
    }
  }, [open, editData]);

  useEffect(() => {
    // Wait until customers finish loading — previously `loading` was always undefined
    // (hook returns `isLoading`), so this cleared the customer before options arrived.
    if (!shop || !customer || customerLoading || !customers) return;
    const selectedCustomerStillValid = filteredCustomersList.some(
      (customerRow) => String(customerRow.id) === String(customer)
    );
    if (!selectedCustomerStillValid) {
      setCustomer("");
    }
  }, [customer, customerLoading, customers, filteredCustomersList, shop]);

  useEffect(() => {
    if (!shop || !saleCurrency) {
      setSelectedAccountId("");
      return;
    }
    if (accounts.length === 0) return;
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [shop, saleCurrency, accounts, sameCurrencyAccounts, selectedAccountId]);

  // When a product is selected, look up its stock record and auto-fill unit, sale_price, and available quantity.
  const handleProductSelect = (index, productId) => {
    const newItems = [...items];
    newItems[index].product = productId;
    const matchingStocks = stocks.filter(
      (stock) =>
        stock.purchase_item_details &&
        stock.purchase_item_details.product &&
        String(stock.purchase_item_details.product.id) === productId &&
        getStockQuantity(stock) > 0 &&
        (!shop || getStockShopId(stock) === String(shop))
    );
    const byCurrency = selectedCurrencyTitle
      ? matchingStocks.filter(
          (s) =>
            normalizeCurrencyCode(s?.currency_details?.title) ===
            normalizeCurrencyCode(selectedCurrencyTitle)
        )
      : matchingStocks;
    const stockRecord = byCurrency[0] || matchingStocks[0];
    if (stockRecord) {
      newItems[index].unit = stockRecord.unit_details
        ? String(stockRecord.unit_details.id)
        : "";
      const details = stockRecord.purchase_item_details || {};
      const buyPrice = getStockPriceByCurrency(stockRecord, matchingStocks, "buy_price", details?.buy_price || "");
      const wholesalePrice = getStockPriceByCurrency(stockRecord, matchingStocks, "wholesale_price", details?.wholesale_price || "");
      const retailPrice = getStockPriceByCurrency(stockRecord, matchingStocks, "retail_price", details?.retail_price || details?.sale_price || stockRecord.sale_price || "");

      newItems[index].sale_price = firstPositivePrice(retailPrice, wholesalePrice, stockRecord.sale_price);
      const stockQty = Number(stockRecord.quantity) || 0;
      const sameOriginalProduct =
        String(newItems[index].original_product || "") === String(productId);
      const originalQty = sameOriginalProduct
        ? Number(newItems[index].original_quantity) || 0
        : 0;
      newItems[index].available = stockQty + originalQty;
      newItems[index].buy_price = buyPrice;
      newItems[index].purchase_item = details?.id || stockRecord.purchase_item || "";
      newItems[index].wholesale_price = wholesalePrice;
      newItems[index].retail_price = retailPrice;
    } else {
      newItems[index].unit = "";
      newItems[index].sale_price = "";
      newItems[index].available = "0";
      newItems[index].buy_price = "";
      newItems[index].purchase_item = "";
      newItems[index].wholesale_price = "";
      newItems[index].retail_price = "";
    }
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        quantity: "",
        unit: "",
        sale_price: "",
        available: "",
        buy_price: "",
        purchase_item: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.sale_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const finalTotal = totalAmount - discountValue;
  const parsedReceived = parseFloat(receivedAmount) || 0;
  const receivedValid = parsedReceived <= finalTotal;
  const needsPaymentAccount = parsedReceived > 0;

  const exchangeRatePreview = useMemo(() => {
    if (!saleCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? saleCurrency;
    if (String(saleCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [saleCurrency, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    if (exchangeRatePreview) {
      setExchangeRate(exchangeRatePreview);
    }
  }, [exchangeRatePreview]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(receivedAmount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [receivedAmount, exchangeRate]);

  const itemsValid =
    items.length > 0 &&
    items.every((item) => item.product && item.quantity && item.sale_price);

  const validate = () => {
    let newErrors = {};
    if (!shop) newErrors.shop = t("sale.shopRequired") || "Shop is required";
    if (!customer)
      newErrors.customer = t("sale.customerRequired") || "Customer is required";
    if (!saleDate)
      newErrors.saleDate =
        t("sale.saleDateRequired") || "Sale date is required";
    if (!saleCurrency)
      newErrors.currency = t("sale.currencyRequired") || "Currency is required";
    if (items.length === 0) {
      newErrors.items =
        t("sale.atLeastOneItemRequired") ||
        "At least one sale item is required";
    } else {
      items.forEach((item, idx) => {
        if (!item.product)
          newErrors[`item_${idx}_product`] =
            t("sale.productRequired") || "Product is required";
        if (!item.unit)
          newErrors[`item_${idx}_unit`] =
            t("sale.unitRequired") || "Unit is required";
        if (!item.quantity)
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityRequired") || "Quantity is required";
        else if (parseFloat(item.quantity) > parseFloat(item.available || "0"))
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityExceedsAvailable") ||
            "Quantity exceeds available stock";
        if (!item.sale_price)
          newErrors[`item_${idx}_salePrice`] =
            t("sale.salePriceRequired") || "Sale price is required";
      });
    }
    if (!receivedValid) {
      newErrors.receivedAmount =
        t("sale.receivedExceedsTotal") ||
        "Received amount should not exceed total amount";
    }
    if (needsPaymentAccount && (!selectedAccountId || !exchangeRate)) {
      newErrors.account =
        t("sale.accountRequired") ||
        "Select an account and exchange rate for the received amount";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      setFormError(
        t("sale.fixValidationFailed") ||
          "Please fix the highlighted fields before saving."
      );
      return;
    }
    setFormError("");
    const payload = {
      shop,
      customer,
      amount: discountValue,
      received_amount: receivedAmount,
      sale_date: saleDate,
      currency: saleCurrency,
      account: needsPaymentAccount ? selectedAccountId : null,
      exchange_rate: needsPaymentAccount ? exchangeRate : "",
      payment_date: saleDate ? `${saleDate}T00:00:00` : new Date().toISOString(),
      payment_notes: paymentNotes,
      notes: notes.trim() || undefined,
      items,
    };
    handleUpdate(editData.id, payload);
  };

  useEffect(() => {
    if (isSuccess) {
      setShop("");
      setCustomer("");
      setDiscount("");
      setSaleDate("");
      setSaleCurrency("");
      setReceivedAmount("");
      setSelectedAccountId("");
      setExchangeRate("");
      setPaymentNotes("");
      setNotes("");
      setItems([]);
      setFormError("");
      onClose(true);
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="lg">
      <DialogTitle>{t("sale.editSale")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          {/* Sale-level fields arranged in two columns */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectShop")}
                fullWidth
                required
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                disabled={shopLoading}
                error={Boolean(errors.shop)}
                helperText={errors.shop}
              >
                {shopsList.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectCustomer")}
                fullWidth
                required
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                disabled={customerLoading}
                error={Boolean(errors.customer)}
                helperText={errors.customer}
              >
                {filteredCustomersList.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("sale.saleDate")}
                type="date"
                fullWidth
                required
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.saleDate)}
                helperText={errors.saleDate}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.currency")}
                fullWidth
                required
                value={saleCurrency}
                onChange={(e) => setSaleCurrency(e.target.value)}
                disabled={currencyLoading}
                error={Boolean(errors.currency)}
                helperText={errors.currency}
              >
                {currenciesList.map((curr) => (
                  <MenuItem key={curr.id} value={String(curr.id)}>
                    {curr.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("sale.discount")}
                type="number"
                fullWidth
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                error={Boolean(errors.discount)}
                helperText={errors.discount}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("sale.note")}
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("sale.notePlaceholder")}
              />
            </Grid>
          </Grid>
          {/* Sale Items Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">{t("sale.saleItems")}</Typography>
            {errors.items && (
              <Typography color="error" variant="body2">
                {errors.items}
              </Typography>
            )}
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              >
                {/* Row 1: Product select and Remove icon */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Autocomplete
                    options={uniqueProductsFromStocks}
                    value={
                      uniqueProductsFromStocks.find(
                        (p) => String(p.id) === String(item.product)
                      ) || null
                    }
                    onChange={(_, value) =>
                      handleProductSelect(index, value ? String(value.id) : "")
                    }
                    getOptionLabel={getProductOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id) === String(value?.id)
                    }
                    disabled={productLoading || stockLoading}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("sale.selectProduct")}
                        required
                        error={Boolean(errors[`item_${index}_product`])}
                        helperText={errors[`item_${index}_product`]}
                      />
                    )}
                  />
                  <IconButton onClick={() => removeItem(index)} color="error">
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Box>
                {/* Row 2: All sale item fields in one row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    label={t("sale.availableQuantity")}
                    value={item.available || ""}
                    sx={{ flex: 1 }}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label={t("sale.quantity")}
                    type="number"
                    required
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, "quantity", e.target.value)
                    }
                    sx={{ flex: 1 }}
                    error={Boolean(errors[`item_${index}_quantity`])}
                    helperText={errors[`item_${index}_quantity`]}
                  />
                    <TextField
                      select
                      label={t("sale.selectUnit")}
                      required
                      value={item.unit}
                    onChange={(e) =>
                      handleItemChange(index, "unit", e.target.value)
                    }
                    sx={{ flex: 1 }}
                    disabled={unitLoading}
                    error={Boolean(errors[`item_${index}_unit`])}
                    helperText={errors[`item_${index}_unit`]}
                  >
                      {unitsList.map((unit) => (
                        <MenuItem key={unit.id} value={String(unit.id)}>
                          {unit.title}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label={t("purchase.buyPrice", "Buy Price")}
                      value={item.buy_price || ""}
                      onChange={(e) =>
                        handleItemChange(index, "buy_price", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      type="number"
                    />
                    <TextField
                      label={t("sale.salePrice")}
                      type="number"
                      required
                      value={item.sale_price}
                    onChange={(e) =>
                      handleItemChange(index, "sale_price", e.target.value)
                    }
                    sx={{ flex: 1 }}
                    error={Boolean(errors[`item_${index}_salePrice`])}
                    helperText={errors[`item_${index}_salePrice`]}
                  />
                </Box>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("sale.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              {t("sale.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("sale.discountAmount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("sale.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("sale.receivedAmount", "Received Amount")}
                  type="number"
                  fullWidth
                  value={receivedAmount}
                  onChange={(e) => setReceivedAmount(e.target.value)}
                  error={Boolean(errors.receivedAmount)}
                  helperText={errors.receivedAmount}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={t("sale.account", "Account")}
                  fullWidth
                  required={needsPaymentAccount}
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={!shop || accountLoading}
                  error={Boolean(errors.account)}
                  helperText={errors.account}
                >
                  {accountLoading ? (
                    <MenuItem value="">
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : accounts.length === 0 ? (
                    <MenuItem value="" disabled>
                      {t("sale.noAccount", "No accounts found")}
                    </MenuItem>
                  ) : (
                    accounts.map((acc) => (
                      <MenuItem key={acc.id} value={String(acc.id)}>
                        {acc.name} ({acc.currency_title || "-"})
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("sale.exchangeRate", "Exchange Rate")}
                  type="number"
                  fullWidth
                  required={needsPaymentAccount}
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  inputProps={{ min: 0, step: "0.000001" }}
                  disabled={!needsPaymentAccount}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("sale.convertedAmount", "Converted Amount")}
                  fullWidth
                  value={convertedAmount}
                  disabled
                />
              </Grid>
              {!exchangeRatePreview && needsPaymentAccount && selectedAccountId && !exchangeRate && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {t("sale.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label={t("sale.paymentNotes", "Payment Notes")}
                  fullWidth
                  multiline
                  rows={2}
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} color="secondary" disabled={loading}>
          {t("sale.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("sale.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleEdit;
